/* eslint-disable no-underscore-dangle */
import { useRouter } from '@application/utils';
import type { FooterGetBreedsQuery } from '@gql/graphql';
import type { RelatedLinksCategory } from '@presentation/components';
import { useMemo } from 'react';

export const MAX_ITEMS_PER_CATEGORY = 10;

export const useFormatInternalLinks = (
  breeds?: FooterGetBreedsQuery['findManyDogBreed'] | null,
) => {
  const { language, translate, routeLocationDescriptor } = useRouter();

  const internalLinks: RelatedLinksCategory[] | undefined = useMemo(() => {
    if (!breeds || !breeds.length) return;
    const litterItems = breeds
      .filter((breed) => breed._count.litters > 0)
      .slice(0, MAX_ITEMS_PER_CATEGORY);
    const litterBreedIds = litterItems.map((breed) => breed.id);

    const breederItems = breeds
      .filter((breed) => !litterBreedIds.includes(breed.id))
      .slice(0, MAX_ITEMS_PER_CATEGORY);

    const breederBreedIds = breederItems.map((breed) => breed.id);
    const otherBreeds = breeds
      .filter((item) => ![...litterBreedIds, ...breederBreedIds].includes(item.id))
      .slice(0, MAX_ITEMS_PER_CATEGORY);

    return [
      {
        label: translate('common.sections.moreLitters.title'),
        items: litterItems.map((breed) => ({
          label: translate('common.sections.moreLittersFromBreeds.title').replace(
            '$breed',
            breed.translations.find((tr) => tr.language === language)?.name ?? breed.name,
          ),
          link: routeLocationDescriptor('litterList', {
            params: [breed.baseBreedName ?? breed.name],
          }),
        })),
      } as RelatedLinksCategory,
      {
        label: translate('common.sections.relatedLinks.breeders.title'),
        items: breederItems.map((breed) => ({
          label: translate('common.sections.relatedLinks.breeders.link').replace(
            '$breeds',
            breed.translations.find((tr) => tr.language === language)?.name ?? breed.name,
          ),

          link: routeLocationDescriptor('breederList', {
            params: [breed.baseBreedName ?? breed.name],
          }),
        })),
      },
      {
        label: translate('common.sections.relatedLinks.breeds.titleOthers'),
        items: otherBreeds.map((breed) => ({
          label: translate('common.sections.relatedLinks.breeds.link').replace(
            '$breed',
            breed.translations.find((tr) => tr.language === language)?.name ?? breed.name,
          ),
          link: routeLocationDescriptor('breed', {
            params: [
              breed.translations.find((tr) => tr.language === language)?.url ??
                breed.baseBreedName ??
                breed.name,
            ],
          }),
        })),
      },
    ] as RelatedLinksCategory[];
  }, [breeds, language, routeLocationDescriptor, translate]);

  return { internalLinks };
};
